import { type GridPosition } from '../../../machine/state/types';
import { getCellElementByIds } from '../utils/get-cell-element-by-ids';

/**
 * Handles moving the focus element based on the given change.
 * This positions the element relative to the scroll container
 * and the grid position given.
 */
export function handleFocusElChange(
  focusEl: HTMLElement,
  scrollableContainer: HTMLElement,
  change: {
    prev: GridPosition | null;
    next: GridPosition | null;
  }
): void {
  if (!change.next) {
    focusEl.style.display = 'none';

    return;
  }

  const focusCellEl = getCellElementByIds({
    entryId: change.next.row.id,
    fieldId: change.next.col.id,
  });

  if (!focusCellEl) {
    return;
  }

  const cellRect = focusCellEl.getBoundingClientRect();
  const scrollableRect = scrollableContainer.getBoundingClientRect();

  /**
   * getBoundingClientRect() gives us viewport-relative coordinates, but our
   * focus element lives inside the scrollableContainer. So we need to:
   *
   * 1. Get the offset from the scrollableContainer (cell position minus scrollableContainer position)
   * 2. Add the scroll offset so it tracks the content, not the viewport
   *
   * Without the scroll offset, the caret would drift when you scroll.
   */
  const x = cellRect.left - scrollableRect.left + scrollableContainer.scrollLeft;
  const y = cellRect.top - scrollableRect.top + scrollableContainer.scrollTop;

  /**
   * We use transform instead of top/left because it's GPU-accelerated and
   * doesn't trigger layout recalculations. Moving the caret should be cheap —
   * just a composite, no reflow.
   */
  focusEl.style.transform = `translate(${x}px, ${y}px)`;
  focusEl.style.width = `${cellRect.width}px`;
  focusEl.style.height = `${cellRect.height}px`;
  focusEl.style.display = 'block';
}
